import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/Logo";
import { PathCard, ProgressBar, StatCard } from "../components/ui";
import { Flame, Zap, BookOpen, Flag, Award, Trophy, Megaphone, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <Spinner label="Loading your dashboard..." />;

  const first = (user?.full_name || "there").split(" ")[0];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <div className="overline mb-2">Welcome back</div>
        <h1 className="font-heading text-3xl font-bold">Hello, {first} 👋</h1>
      </div>

      {/* XP / level bento */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="card p-6 lg:col-span-2 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="relative flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3">
                <span className="font-heading text-4xl font-extrabold text-[#F26522]">{data.xp}</span>
                <span className="text-slate-400">XP</span>
                <span className="chip bg-[#F26522]/15 text-[#F26522] font-bold">Level {data.level}</span>
              </div>
              <div className="mt-4 w-64 max-w-full">
                <div className="flex justify-between text-xs text-slate-400 mb-1.5">
                  <span>{data.xp_into_level} / {data.xp_for_next_level} XP</span>
                  <span>Level {data.level + 1}</span>
                </div>
                <ProgressBar pct={data.progress_pct} />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="flex items-center gap-1.5 text-orange-400 font-heading text-2xl font-bold"><Flame size={22} /> {data.streak}</div>
                <div className="text-xs text-slate-400 mt-1">Day streak</div>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1.5 text-[#38bdf8] font-heading text-2xl font-bold"><Trophy size={20} /> #{data.leaderboard_rank || "—"}</div>
                <div className="text-xs text-slate-400 mt-1">Global rank</div>
              </div>
            </div>
          </div>
        </div>
        <div className="card p-6 flex flex-col justify-center">
          <div className="flex items-center gap-2 text-slate-300 mb-2"><Zap size={18} className="text-[#F26522]" /> Daily goal</div>
          <div className="font-heading text-2xl font-bold">{Math.min(data.xp_into_level, data.daily_goal_xp)} / {data.daily_goal_xp} XP</div>
          <ProgressBar pct={Math.min(100, (data.xp_into_level / data.daily_goal_xp) * 100)} className="mt-3" />
          <p className="text-slate-400 text-xs mt-3">Keep your streak alive — complete a module or solve a challenge today.</p>
        </div>
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={BookOpen} label="Modules done" value={data.stats.modules_completed} testid="stat-modules" />
        <StatCard icon={Zap} label="Quizzes passed" value={data.stats.quizzes_passed} accent="#0ea5e9" testid="stat-quizzes" />
        <StatCard icon={Flag} label="CTF solved" value={data.stats.ctf_solved} accent="#10B981" testid="stat-ctf" />
        <StatCard icon={Award} label="Certificates" value={data.stats.certificates} accent="#F59E0B" testid="stat-certs" />
        <StatCard icon={Trophy} label="Badges" value={data.stats.badges} accent="#a855f7" testid="stat-badges" />
      </div>

      {/* Continue learning */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold">Continue learning</h2>
          <Link to="/app/learning" className="text-sm text-[#38bdf8] hover:underline flex items-center gap-1">Browse all <ArrowRight size={14} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.continue_learning.map((p) => <PathCard key={p.id} p={p} to={`/app/learning/${p.id}`} />)}
        </div>
      </div>

      {/* Announcements */}
      {data.announcements.length > 0 && (
        <div>
          <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2"><Megaphone size={18} className="text-[#F26522]" /> Latest announcements</h2>
          <div className="space-y-3">
            {data.announcements.map((a) => (
              <div key={a.id} className="card p-5" data-testid="dashboard-announcement">
                <h3 className="font-semibold">{a.title}</h3>
                <p className="text-slate-400 text-sm mt-1">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
